import {
  deleteField,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  type Firestore,
} from 'firebase/firestore'
import type {
  CloudMovieRecord,
  CloudSeriesRecord,
  LibraryAdapter,
  MovieEntry,
  MovieUpdates,
  SeasonEpisodeIds,
  SeriesEntry,
  SeriesUpdates,
  Unsubscribe,
} from '@govu/shared'

interface DepsLike {
  firestore: Firestore
  /**
   * Returns the currently signed-in uid, or null if signed out.
   * Caller is responsible for re-creating the adapter when the user
   * changes (we don't subscribe to auth state from in here, on purpose
   * — keeps the adapter pure and side-effect-free).
   */
  getUid: () => string | null
}

/**
 * Concrete LibraryAdapter that talks to Firestore via the standard
 * `users/{uid}/library/movies` and `users/{uid}/library/series` single-
 * document layouts the desktop app already syncs.
 *
 * This is intentionally a thin wrapper. Every operation is exactly the
 * Firestore call that already lived in `useLibrary` / `useWriteLibrary`,
 * just packaged behind the LibraryAdapter interface so any consumer
 * (PWA or future shared UI) can use it interchangeably with the
 * eventual desktop IpcLibraryAdapter.
 */
export function createFirestoreLibraryAdapter(deps: DepsLike): LibraryAdapter {
  const { firestore, getUid } = deps

  const requireUid = (): string => {
    const uid = getUid()
    if (!uid) throw new Error('FirestoreLibraryAdapter: not signed in')
    return uid
  }

  const moviesDoc = (uid: string) => doc(firestore, `users/${uid}/library/movies`)
  const seriesDoc = (uid: string) => doc(firestore, `users/${uid}/library/series`)

  return {
    // ────────────────────────────────────────────────────────────────
    // Movies
    // ────────────────────────────────────────────────────────────────
    subscribeMovies(callback: (entries: MovieEntry[]) => void): Unsubscribe {
      const uid = getUid()
      if (!uid) {
        // Signed out — emit empty and return a no-op unsubscribe.
        callback([])
        return () => {}
      }
      return onSnapshot(
        moviesDoc(uid),
        (snap) => {
          if (!snap.exists()) {
            callback([])
            return
          }
          const data = snap.data() as Record<string, CloudMovieRecord>
          const list: MovieEntry[] = []
          for (const [key, value] of Object.entries(data)) {
            const tmdbId = parseInt(key, 10)
            if (Number.isNaN(tmdbId) || !value || typeof value !== 'object') continue
            list.push({ tmdbId, record: value })
          }
          list.sort((a, b) => {
            const aDate = a.record.dateAdded ? Date.parse(a.record.dateAdded) : 0
            const bDate = b.record.dateAdded ? Date.parse(b.record.dateAdded) : 0
            return bDate - aDate
          })
          callback(list)
        },
        (err) => {
          console.error('[FirestoreLibraryAdapter:movies]', err)
          callback([])
        }
      )
    },

    async addMovie(tmdbId: number): Promise<void> {
      const uid = requireUid()
      const now = new Date().toISOString()
      const payload: Record<string, any> = {
        [tmdbId]: {
          progress: 0,
          isSeen: false,
          userRating: null,
          dateAdded: now,
          tags: [],
          notes: null,
          isVirtual: true,
          ownerDeviceIds: [],
          updatedAt: now,
        },
      }
      await setDoc(moviesDoc(uid), payload, { merge: true })
    },

    async removeMovie(tmdbId: number): Promise<void> {
      const uid = requireUid()
      await updateDoc(moviesDoc(uid), { [String(tmdbId)]: deleteField() })
    },

    async updateMovie(tmdbId: number, changes: MovieUpdates): Promise<void> {
      const uid = requireUid()
      const payload: Record<string, any> = {
        [tmdbId]: { ...changes, updatedAt: new Date().toISOString() },
      }
      await setDoc(moviesDoc(uid), payload, { merge: true })
    },

    // ────────────────────────────────────────────────────────────────
    // Series
    // ────────────────────────────────────────────────────────────────
    subscribeSeries(callback: (entries: SeriesEntry[]) => void): Unsubscribe {
      const uid = getUid()
      if (!uid) {
        callback([])
        return () => {}
      }
      return onSnapshot(
        seriesDoc(uid),
        (snap) => {
          if (!snap.exists()) {
            callback([])
            return
          }
          const data = snap.data() as Record<string, CloudSeriesRecord>
          const list: SeriesEntry[] = []
          for (const [key, value] of Object.entries(data)) {
            const tmdbId = parseInt(key, 10)
            if (Number.isNaN(tmdbId) || !value || typeof value !== 'object') continue
            list.push({ tmdbId, record: value })
          }
          list.sort((a, b) => {
            const aDate = a.record.lastUpdated ? Date.parse(a.record.lastUpdated) : 0
            const bDate = b.record.lastUpdated ? Date.parse(b.record.lastUpdated) : 0
            return bDate - aDate
          })
          callback(list)
        },
        (err) => {
          console.error('[FirestoreLibraryAdapter:series]', err)
          callback([])
        }
      )
    },

    async addSeries(tmdbId: number): Promise<void> {
      const uid = requireUid()
      const now = new Date().toISOString()
      const payload: Record<string, any> = {
        [tmdbId]: {
          userRating: null,
          isFavorite: false,
          tags: [],
          notes: null,
          isVirtual: true,
          ownerDeviceIds: [],
          lastUpdated: now,
          episodes: {},
        },
      }
      await setDoc(seriesDoc(uid), payload, { merge: true })
    },

    async removeSeries(tmdbId: number): Promise<void> {
      const uid = requireUid()
      await updateDoc(seriesDoc(uid), { [String(tmdbId)]: deleteField() })
    },

    async updateSeries(tmdbId: number, changes: SeriesUpdates): Promise<void> {
      const uid = requireUid()
      const payload: Record<string, any> = {
        [tmdbId]: { ...changes, lastUpdated: new Date().toISOString() },
      }
      await setDoc(seriesDoc(uid), payload, { merge: true })
    },

    async setEpisodeWatched(
      seriesTmdbId: number,
      seasonNumber: number,
      episodeNumber: number,
      watched: boolean
    ): Promise<void> {
      const uid = requireUid()
      const now = new Date().toISOString()
      const payload: Record<string, any> = {
        [seriesTmdbId]: {
          episodes: {
            [seasonNumber]: {
              [episodeNumber]: {
                isWatched: watched,
                lastWatched: watched ? now : null,
              },
            },
          },
          lastUpdated: now,
        },
      }
      await setDoc(seriesDoc(uid), payload, { merge: true })
    },

    async markWatchedUntil(
      seriesTmdbId: number,
      seasons: SeasonEpisodeIds[],
      targetSeason: number,
      targetEpisode: number
    ): Promise<void> {
      const uid = requireUid()
      const now = new Date().toISOString()
      const episodes: Record<string, Record<string, unknown>> = {}
      for (const season of seasons) {
        if (season.seasonNumber > targetSeason) break
        const seasonKey = String(season.seasonNumber)
        episodes[seasonKey] = {}
        for (const epNum of season.episodeNumbers) {
          if (season.seasonNumber === targetSeason && epNum > targetEpisode) break
          episodes[seasonKey][String(epNum)] = {
            isWatched: true,
            lastWatched: now,
          }
        }
      }
      const payload: Record<string, any> = {
        [seriesTmdbId]: { episodes, lastUpdated: now },
      }
      await setDoc(seriesDoc(uid), payload, { merge: true })
    },
  }
}
